(() => {
  if (customElements.get('holo-badge')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const needsOrientationPermission = typeof DeviceOrientationEvent !== 'undefined'
    && typeof DeviceOrientationEvent.requestPermission === 'function';
  const clamp = (value) => Math.min(1, Math.max(0, value));

  const badges = new Set();
  let pointer = null;
  let gyro = null;
  let frame = 0;
  let listening = false;

  const source = () => {
    if (reducedMotion.matches) return 'static';
    if (finePointer.matches) return pointer ? 'pointer' : 'scroll';
    return gyro ? 'tilt' : 'scroll';
  };

  const tick = () => {
    frame = 0;
    let moving = false;
    badges.forEach((badge) => {
      if (badge.visible && badge.step()) moving = true;
    });
    if (moving) frame = window.requestAnimationFrame(tick);
  };

  const update = () => {
    const mode = source();
    if (mode === 'static') {
      badges.forEach((badge) => badge.reset());
      return;
    }

    const viewportHeight = window.innerHeight;
    badges.forEach((badge) => {
      if (!badge.visible) return;

      const rect = badge.getBoundingClientRect();
      let x = 0.5;
      let y = 0.5;
      let inside = false;

      if (mode === 'pointer') {
        x = clamp((pointer.x - rect.left) / rect.width);
        y = clamp((pointer.y - rect.top) / rect.height);
        inside = pointer.x >= rect.left && pointer.x <= rect.right && pointer.y >= rect.top && pointer.y <= rect.bottom;
      } else if (mode === 'scroll') {
        const progress = clamp((rect.top + rect.height / 2) / viewportHeight);
        x = 0.2 + 0.6 * (1 - progress);
        y = 1 - progress;
      } else {
        x = gyro.x;
        y = gyro.y;
        inside = true;
      }

      badge.setTarget(x, y, inside);
    });

    if (!frame) frame = window.requestAnimationFrame(tick);
  };

  const onPointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    pointer = { x: event.clientX, y: event.clientY };
    update();
  };

  const onPointerLeave = () => {
    if (!pointer) return;
    pointer = null;
    update();
  };

  const onOrientation = (event) => {
    if (event.gamma == null || event.beta == null) return;
    gyro = { x: clamp(0.5 + event.gamma / 45), y: clamp(0.5 + (event.beta - 40) / 45) };
    update();
  };

  const requestOrientation = () => {
    DeviceOrientationEvent.requestPermission().catch(() => {}).then(update);
  };

  const onReducedMotionChange = () => update();

  const startListening = () => {
    if (listening) return;
    listening = true;
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('pointerleave', onPointerLeave);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    window.addEventListener('deviceorientation', onOrientation);
    reducedMotion.addEventListener?.('change', onReducedMotionChange);
    if (needsOrientationPermission && !finePointer.matches) {
      document.addEventListener('click', requestOrientation, { once: true });
    }
  };

  const stopListening = () => {
    if (!listening || badges.size) return;
    listening = false;
    window.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerleave', onPointerLeave);
    window.removeEventListener('scroll', update);
    window.removeEventListener('resize', update);
    window.removeEventListener('deviceorientation', onOrientation);
    reducedMotion.removeEventListener?.('change', onReducedMotionChange);
    document.removeEventListener('click', requestOrientation);
    window.cancelAnimationFrame(frame);
    frame = 0;
  };

  class HoloBadge extends HTMLElement {
    constructor() {
      super();
      this.onIntersect = this.onIntersect.bind(this);
      this.onResize = this.onResize.bind(this);
      this.fit = this.fit.bind(this);
      this.x = 0.5;
      this.y = 0.5;
      this.targetX = 0.5;
      this.targetY = 0.5;
      this.live = false;
      this.visible = false;
    }

    connectedCallback() {
      this.tilt = this.hasAttribute('data-holo-tilt');
      this.fitTargets = Array.from(this.querySelectorAll('[data-holo-fit]'));
      this.observer = new IntersectionObserver(this.onIntersect);
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);
      badges.add(this);
      startListening();
      update();
      this.fit();
      document.fonts?.ready.then(this.fit);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.fitFrame);
      badges.delete(this);
      stopListening();
    }

    onIntersect(entries) {
      this.visible = entries.some((entry) => entry.isIntersecting);
      if (this.visible) update();
    }

    onResize() {
      window.cancelAnimationFrame(this.fitFrame);
      this.fitFrame = window.requestAnimationFrame(this.fit);
    }

    // Scales down texts that would overflow the badge: --holo-fit multiplies their font-size.
    fit() {
      if (!this.isConnected || !this.fitTargets?.length) return;
      this.fitTargets.forEach((target) => {
        target.style.removeProperty('--holo-fit');
        const available = target.clientWidth;
        const needed = target.scrollWidth;
        if (available > 0 && needed > available) {
          target.style.setProperty('--holo-fit', (Math.floor((available / needed) * 100) / 100).toFixed(2));
        }
      });
    }

    setTarget(x, y, inside) {
      this.targetX = x;
      this.targetY = y;
      this.live = inside;
      this.classList.remove('is-static');
    }

    step() {
      this.x += (this.targetX - this.x) * 0.16;
      this.y += (this.targetY - this.y) * 0.16;
      this.style.setProperty('--hx', this.x.toFixed(3));
      this.style.setProperty('--hy', this.y.toFixed(3));
      if (this.tilt) {
        this.style.setProperty('--rx', this.live ? `${((this.y - 0.5) * -12).toFixed(2)}deg` : '0deg');
        this.style.setProperty('--ry', this.live ? `${((this.x - 0.5) * 14).toFixed(2)}deg` : '0deg');
      }
      return Math.abs(this.targetX - this.x) > 0.002 || Math.abs(this.targetY - this.y) > 0.002;
    }

    reset() {
      ['--hx', '--hy', '--rx', '--ry'].forEach((property) => this.style.removeProperty(property));
      this.x = this.targetX = 0.5;
      this.y = this.targetY = 0.5;
      this.classList.add('is-static');
    }
  }

  customElements.define('holo-badge', HoloBadge);
})();
